import { locales } from "@/config/locale";

/** The registry's codes as the prefix lookup, so the path check never carries a second list. */
const localePrefixes: ReadonlySet<string> = new Set(locales);

/**
 * `de/|en/|es/|…`, built from the registry. The group is optional because the default locale
 * (`de`) is served without a URL prefix, exactly as the four-element alternation was before.
 */
const localeAlternation = locales.map((locale) => `${locale}/`).join("|");

/** Anchored at the start and tolerant of the path's end, built from the registry, never a literal. */
const RECOVERY_PATH = new RegExp(`^/(?:${localeAlternation})?checkout/recover(?:/|$)`);

/**
 * Affiliate- und Fulfillment-Admin liegen unter `/[locale]/admin/...` (nicht channel-scoped).
 * Dort soll posthog-js keine Events senden und keine Session Recordings erzeugen.
 *
 * Both checks read the registry's `locales`: a locale the storefront knows but this helper does
 * not would send analytics from exactly the paths this function exists to keep quiet.
 */
export function isPostHogSuppressedPath(pathname: string): boolean {
	const segments = pathname.split("/").filter(Boolean);
	const first = localePrefixes.has(segments[0]) ? segments[1] : segments[0];
	return first === "admin" || isRecoveryPath(pathname);
}

export function isRecoveryPath(pathname: string): boolean {
	return RECOVERY_PATH.test(pathname);
}

/** Also covers queued replay metadata captured before a client-side route change. */
export function containsRecoveryCapability(value: unknown): boolean {
	const pending: unknown[] = [value];
	const visited = new Set<object>();
	let count = 0;
	while (pending.length) {
		if (++count > 10000) return true;
		const current = pending.pop();
		if (
			typeof current === "string" &&
			(/\/checkout\/recover(?:[\/#?]|$)/.test(current) || /[#&]recovery=/.test(current))
		)
			return true;
		if (current && typeof current === "object" && !visited.has(current)) {
			visited.add(current);
			pending.push(...Object.values(current));
		}
	}
	return false;
}
